import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../core/scroll-lock";
import { adminUpdateBoard } from "../core/axios/endpoints";

/**
 * Bordinstellingen: naam en key. Een key-wijziging nummert alle aanvragen op
 * het bord om (SCN-12 wordt SOL-12); oude keys blijven vindbaar.
 */
const BoardSettingsModal = ({ board, isOpen, onClose, onSaved }) => {
  const [name, setName] = useState(board?.name ?? "");
  const [key, setKey] = useState(board?.key ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!isOpen) return undefined;
    setName(board?.name ?? "");
    setKey(board?.key ?? "");
    setError("");
    lockBodyScroll();
    // busy via ref: Escape mag de modal niet sluiten terwijl de PATCH loopt.
    const onKey = (e) => e.key === "Escape" && !busyRef.current && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [isOpen, board?._id, board?.name, board?.key, onClose]);

  if (!isOpen) return null;

  const keyChanged = key.trim().toUpperCase() !== (board?.key ?? "");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { name: name.trim() };
      if (keyChanged) payload.key = key.trim().toUpperCase();
      const res = await adminUpdateBoard(board._id, payload);
      onSaved?.(res?.data ?? res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error ?? err?.response?.data?.message ?? "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 9999 }} className="flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#091E428A]" onClick={() => !busy && onClose?.()} />
      <form onSubmit={submit} className="relative !mt-0 w-full max-w-md rounded-md bg-white p-6 shadow-overlay">
        <h3 className="text-xl font-medium text-ink">Bordinstellingen</h3>
        <p className="mt-1 text-sm text-ink-subtlest">
          {board?.kind === "direct"
            ? "Het bord voor aanvragen zonder partner."
            : `Bord van ${board?.clientUserId?.bedrijfsnaam || board?.clientUserId?.naam || "deze klant"}.`}
        </p>

        <label className="mt-5 block">
          <span className="text-xs font-semibold text-ink-subtlest">Naam</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            required
            className="mt-1 block w-full !rounded !border-2 !border-solid !border-line !bg-white !px-2 !py-1.5 text-sm text-ink focus:!border-brand focus:outline-none"
          />
        </label>

        <label className="mt-4 block">
          <span className="text-xs font-semibold text-ink-subtlest">Key</span>
          <input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
            pattern="[A-Z][A-Z0-9]{1,9}"
            required
            className="mt-1 block w-full !rounded !border-2 !border-solid !border-line !bg-white !px-2 !py-1.5 text-sm font-medium uppercase text-ink focus:!border-brand focus:outline-none"
          />
          <span className="mt-1 block text-xs text-ink-subtlest">
            Begint met een letter, 2 t/m 10 hoofdletters of cijfers. Aanvragen heten dan {key || "KEY"}-1, {key || "KEY"}-2, enz.
          </span>
        </label>

        {keyChanged ? (
          <div className="mt-3 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
            Alle aanvragen op dit bord krijgen de nieuwe key. De oude keys blijven vindbaar in de zoekbalk.
          </div>
        ) : null}

        {error ? <div className="mt-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">{error}</div> : null}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50">
            Annuleren
          </button>
          <button type="submit" disabled={busy} className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
  return createPortal(modal, document.body);
};

export default BoardSettingsModal;
